import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Loader2, Mail, Send, Save, Plus, Trash2, History, MessageCircle,
  CheckCircle2, PackageCheck, Link2, ShieldCheck, Unlock, Copy, ExternalLink, KeyRound, RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { StatusPill } from "./AdminPrioritisedCustomers";

/* Shared Priority Order workspace — the order and the message thread on the
   same screen. Every edit writes to the same console_orders record and is
   logged to the change history. */

const AUB = "#3E1F46";
const EDGE = "#E8DFCB";
const when = (iso) => iso ? new Date(iso).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

export default function AdminPriorityWorkspace() {
  const { customerId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [draft, setDraft] = useState("");
  const [linkId, setLinkId] = useState("");
  const [inviteTpl, setInviteTpl] = useState(null);

  useEffect(() => {
    api.get("/admin/mail-merge")
      .then((r) => setInviteTpl(r.data.templates.find((t) => t.key === "priority_invite") || null))
      .catch(() => {});
  }, []);

  const load = async () => {
    try {
      const r = await api.get(`/admin/priority/customers/${customerId}`);
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open that customer."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't work."); }
    finally { setBusy(""); }
  };

  const setStatus = (status) => act(`status-${status}`,
    () => api.post(`/admin/priority/customers/${customerId}/status`, { status }), `Status set to ${status}.`);

  const invite = () => act("invite", async () => {
    const r = await api.post(`/admin/priority/customers/${customerId}/invite`);
    toast[r.data?.sent ? "success" : "warning"](r.data?.sent
      ? `Priority invite emailed to ${r.data.email}.`
      : `Saved to the outbox — ${r.data?.detail || "email provider unavailable"}.`);
  });

  const sendMessage = () => {
    if (!draft.trim()) return;
    return act("message", async () => {
      await api.post(`/admin/priority/customers/${customerId}/messages`, { body: draft });
      setDraft("");
    });
  };

  const linkOrder = () => {
    if (!linkId.trim()) return toast.error("Paste the order id to link.");
    return act("link", async () => {
      await api.post(`/admin/priority/customers/${customerId}/link-order`, { order_id: linkId.trim() });
      setLinkId("");
    }, "Order linked to this customer.");
  };

  if (loading) return <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading the priority workspace…</div>;
  if (!data) return null;

  const { customer, summary, orders, messages, history, statuses } = data;
  const firstName = (customer.name || "there").split(" ")[0];
  const inviteBody = inviteTpl ? inviteTpl.body.replace("Hi there,", `Hi ${firstName},`) : "";
  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${inviteTpl.subject}\n\n${inviteBody}`);
      toast.success("Invite email copied — paste it straight into Gmail.");
    } catch { toast.error("Clipboard blocked — use the Gmail draft button instead."); }
  };
  const gmailDraft = inviteTpl
    ? `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(customer.email || "")}&su=${encodeURIComponent(inviteTpl.subject)}&body=${encodeURIComponent(inviteBody)}`
    : "#";

  return (
    <div className="space-y-5 max-w-6xl" data-testid="priority-workspace">
      <Link to="/admin/customers/priority" data-testid="priority-back"
        className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#6E1E3A]">
        <ArrowLeft className="w-3.5 h-3.5" /> All priority customers
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.42em] uppercase font-semibold text-[#6E1E3A]">✦ Priority order</div>
          <h1 className="font-serif text-4xl mt-1" style={{ color: AUB }} data-testid="priority-customer-name">{customer.name || customer.email}</h1>
          <div className="text-xs text-[#7A6A55] mt-1 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {customer.email || "no email"}</span>
            {customer.phone && <span>{customer.phone}</span>}
            <span>In queue since {when(customer.priority_since)}</span>
            <span>Invite sent {when(customer.invited_at)}</span>
          </div>
        </div>
        <div className="text-right space-y-1.5">
          <StatusPill status={customer.priority_status} testid="priority-workspace-status" />
          <div className="font-serif text-3xl text-[#6E1E3A]" data-testid="priority-summary-value">NZ${Number(summary.total_value).toFixed(2)}</div>
          <div className="text-xs text-[#7A6A55]">
            {summary.order_count} order{summary.order_count === 1 ? "" : "s"} · paid ${Number(summary.paid_value).toFixed(2)} · owing ${Number(summary.owing_value).toFixed(2)}
          </div>
        </div>
      </header>

      {customer.confirmed_at && (
        <div className="bg-[#EAF3EC] border-l-4 border-[#1F5B3F] p-3 text-sm text-[#1F5B3F]" data-testid="priority-confirmed-banner">
          <ShieldCheck className="w-4 h-4 inline mr-1.5 mb-0.5" />
          Customer confirmed their order is complete and correct on {when(customer.confirmed_at)}. That version is locked.
        </div>
      )}

      <section className="bg-white border rounded p-4 flex flex-wrap gap-2 items-center" style={{ borderColor: EDGE }}>
        <span className="text-[10px] tracking-[0.25em] uppercase font-semibold text-[#9C8B73] mr-1">Workflow</span>
        {statuses.map((s) => (
          <button key={s} type="button" onClick={() => setStatus(s)} disabled={busy === `status-${s}` || customer.priority_status === s}
            data-testid={`priority-set-${s.replace(/\s+/g, "-").toLowerCase()}`}
            className="px-3 h-8 border rounded text-[10px] font-bold uppercase tracking-[0.12em] text-[#3C2A1E] hover:border-[#6E1E3A] disabled:opacity-30"
            style={{ borderColor: EDGE }}>{s}</button>
        ))}
        <div className="w-full flex flex-wrap gap-2 pt-2 border-t mt-2" style={{ borderColor: EDGE }}>
          <button type="button" onClick={invite} disabled={busy === "invite"} data-testid="priority-invite"
            className="inline-flex items-center gap-1.5 px-4 h-9 border border-[#C49247] text-[#8A5D12] bg-white text-[10px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
            {busy === "invite" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            {customer.invited_at ? "Resend priority invite" : "Send priority invite"}
          </button>
          {inviteTpl && (
            <>
              <button type="button" onClick={copyInvite} data-testid="priority-copy-invite"
                title="Copy the invite email so you can send it from Gmail yourself"
                className="inline-flex items-center gap-1.5 px-4 h-9 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E] bg-white" style={{ borderColor: EDGE }}>
                <Copy className="w-3.5 h-3.5" /> Copy invite email
              </button>
              <a href={gmailDraft} target="_blank" rel="noopener noreferrer" data-testid="priority-gmail-draft"
                className="inline-flex items-center gap-1.5 px-4 h-9 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#6E1E3A] bg-white" style={{ borderColor: EDGE }}>
                <ExternalLink className="w-3.5 h-3.5" /> Open Gmail draft
              </a>
            </>
          )}
          <button type="button" data-testid="priority-login-link" disabled={busy === "login-link"}
            title="Generate a one-time login link and copy it"
            onClick={() => act("login-link", async () => {
              const r = await api.post(`/admin/customers/${customerId}/login-link`);
              await navigator.clipboard.writeText(r.data.url).catch(() => {});
              toast.success(r.data.emailed
                ? `Login link emailed to ${r.data.email} and copied to your clipboard.`
                : `Login link copied , email is down, so paste it to them yourself. Valid ${r.data.expires_in_hours}h.`);
            })}
            className="inline-flex items-center gap-1.5 px-4 h-9 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E] bg-white" style={{ borderColor: EDGE }}>
            <KeyRound className="w-3.5 h-3.5" /> Send login link
          </button>
          <button type="button" data-testid="priority-reset-access" disabled={busy === "reset-access"}
            title="Create or reset their HQ login and copy a temporary password"
            onClick={() => act("reset-access", async () => {
              const r = await api.post(`/console/customers/${customerId}/generate-access`);
              const pw = r.data?.temp_password || "";
              await navigator.clipboard.writeText(`Heritage HQ login\nEmail: ${r.data?.email}\nTemporary password: ${pw}`).catch(() => {});
              toast.success(`Access reset , login details copied to your clipboard.`);
            })}
            className="inline-flex items-center gap-1.5 px-4 h-9 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#8A5D12] bg-white" style={{ borderColor: "#C49247" }}>
            <RefreshCcw className="w-3.5 h-3.5" /> Reset customer access
          </button>
          <button type="button" onClick={() => act("confirm-packing", () => api.post(`/admin/priority/customers/${customerId}/confirm-for-packing`), "Confirmed for packing.")}
            disabled={busy === "confirm-packing"} data-testid="priority-confirm-packing"
            className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#3E1F46] text-white text-[10px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
            <CheckCircle2 className="w-3.5 h-3.5" /> Confirm order for packing
          </button>
          <button type="button" onClick={() => act("send-packing", () => api.post(`/admin/priority/customers/${customerId}/send-to-packing`), "Orders moved into packing.")}
            disabled={busy === "send-packing"} data-testid="priority-send-packing"
            className="inline-flex items-center gap-1.5 px-4 h-9 bg-[#1F5B3F] text-white text-[10px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
            <PackageCheck className="w-3.5 h-3.5" /> Send to packing
          </button>
          {orders.some((o) => o.priority_locked) && (
            <button type="button" data-testid="priority-reopen"
              onClick={() => act("reopen", async () => {
                for (const o of orders.filter((x) => x.priority_locked)) {
                  await api.patch(`/admin/priority/orders/${o.id}`, { reopen: true });
                }
              }, "Reopened — the customer can edit again.")}
              disabled={busy === "reopen"}
              className="inline-flex items-center gap-1.5 px-4 h-9 border border-[#8A5D12] text-[#8A5D12] bg-white text-[10px] font-bold uppercase tracking-[0.14em] disabled:opacity-40">
              <Unlock className="w-3.5 h-3.5" /> Reopen for customer
            </button>
          )}
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-[1.35fr_0.65fr] items-start">
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div className="bg-white border border-dashed p-8 text-center text-sm italic text-[#7A6A55]" style={{ borderColor: EDGE }} data-testid="priority-no-orders">
              No orders linked to this customer yet — paste an order id below to link one.
            </div>
          ) : orders.map((o) => (
            <OrderCard key={o.id} order={o} onSaved={load} />
          ))}

          <div className="bg-white border rounded p-4 flex flex-wrap items-end gap-2" style={{ borderColor: EDGE }}>
            <label className="text-xs flex-1 min-w-[220px]">
              <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Link an existing / historical order (order id)</span>
              <input value={linkId} onChange={(e) => setLinkId(e.target.value)} data-testid="priority-link-input"
                className="w-full h-10 px-3 border bg-white text-sm" style={{ borderColor: EDGE }} />
            </label>
            <button type="button" onClick={linkOrder} disabled={busy === "link"} data-testid="priority-link-order"
              className="h-10 px-4 border text-[10px] font-bold uppercase tracking-[0.14em] text-[#3C2A1E] disabled:opacity-40" style={{ borderColor: EDGE }}>
              <Link2 className="w-3.5 h-3.5 inline mr-1" /> Link order
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <section className="bg-white border rounded flex flex-col" style={{ borderColor: EDGE }} data-testid="priority-thread">
            <div className="px-4 py-3 border-b text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] inline-flex items-center gap-1.5" style={{ borderColor: EDGE }}>
              <MessageCircle className="w-4 h-4" /> Message thread
            </div>
            <div className="p-3 space-y-2 max-h-[420px] overflow-y-auto">
              {messages.length === 0 && <p className="text-xs italic text-[#9C8B73] px-1">No messages yet — say hello.</p>}
              {messages.map((m) => (
                <div key={m.id} data-testid={`priority-message-${m.id}`}
                  className={`p-2.5 rounded text-sm ${m.sender === "admin" ? "bg-[#F2EAF3] ml-6" : "bg-[#FBF6E9] mr-6"}`}>
                  <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#9C8B73] flex justify-between gap-2">
                    <span>{m.sender === "admin" ? "Heritage" : (m.sender_name || "Customer")}</span>
                    <span>{when(m.created_at)}{m.sender === "customer" ? (m.read_by_admin ? " · read" : " · new") : (m.read_by_customer ? " · read" : " · unread")}</span>
                  </div>
                  <div className="mt-1 whitespace-pre-wrap text-[#3C2A1E]">{m.body}</div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t space-y-2" style={{ borderColor: EDGE }}>
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} placeholder="Message this customer…"
                data-testid="priority-message-input" className="w-full p-2.5 border bg-white text-sm" style={{ borderColor: EDGE }} />
              <button type="button" onClick={sendMessage} disabled={busy === "message" || !draft.trim()} data-testid="priority-message-send"
                className="w-full h-10 bg-[#6E1E3A] text-white text-[11px] font-bold uppercase tracking-[0.14em] inline-flex items-center justify-center gap-2 disabled:opacity-40">
                {busy === "message" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send message
              </button>
            </div>
          </section>

          <section className="bg-white border rounded" style={{ borderColor: EDGE }} data-testid="priority-history">
            <div className="px-4 py-3 border-b text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A] inline-flex items-center gap-1.5" style={{ borderColor: EDGE }}>
              <History className="w-4 h-4" /> Change history
            </div>
            <ul className="divide-y max-h-[320px] overflow-y-auto" style={{ borderColor: "#F0E8D8" }}>
              {history.length === 0 && <li className="px-4 py-3 text-xs italic text-[#9C8B73]">Nothing changed yet.</li>}
              {history.map((h) => (
                <li key={h.id} className="px-4 py-2.5 text-xs" data-testid={`priority-history-${h.id}`}>
                  <div className="font-semibold text-[#3C2A1E]">{h.who}</div>
                  <div className="text-[#7A6A55]">{h.what}</div>
                  <div className="text-[10px] text-[#9C8B73]">{when(h.created_at)}</div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function OrderCard({ order, onSaved }) {
  const [items, setItems] = useState(order.line_items.map((i) => ({ ...i })));
  const [backups, setBackups] = useState((order.backup_varieties || []).join(", "));
  const [form, setForm] = useState({
    payment_status: order.payment_status,
    amount_paid: order.amount_paid,
    total: order.total,
    shipping_price: order.shipping,
    discount: order.discount,
    shipping_address: typeof order.shipping_address === "string" ? order.shipping_address : "",
    admin_notes: order.admin_notes,
    customer_visible_notes: order.customer_visible_notes,
    notes: order.notes,
  });
  const [busy, setBusy] = useState(false);

  const owing = useMemo(() => Math.max(0, Number(form.total || 0) - Number(form.amount_paid || 0)), [form]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/priority/orders/${order.id}`, {
        line_items: items.map((i) => ({ name: i.name, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0 })),
        backup_varieties: backups.split(",").map((b) => b.trim()).filter(Boolean),
        ...form,
        amount_paid: Number(form.amount_paid) || 0,
        total: Number(form.total) || 0,
        shipping_price: Number(form.shipping_price) || 0,
        discount: Number(form.discount) || 0,
      });
      toast.success(`Order ${order.order_number} saved.`);
      await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that order."); }
    finally { setBusy(false); }
  };

  return (
    <article className="bg-white border rounded p-4 space-y-3" style={{ borderColor: EDGE }} data-testid={`priority-order-${order.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <strong className="font-serif text-[17px]" style={{ color: AUB }}>#{order.order_number}</strong>
        <span className="text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded bg-[#F3EBDD] text-[#6E1E3A] font-semibold">{order.kind}{order.mystery_tier ? ` · ${order.mystery_tier}` : ""}</span>
        <span className="text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded bg-[#3E1F46] text-white font-semibold">{order.stage}</span>
        {order.source === "customer_priority" && <span className="text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded bg-[#C49247] text-[#3C2A1E] font-semibold" data-testid={`priority-order-customer-added-${order.id}`}>Added by customer</span>}
        {order.priority_locked && <span className="text-[10px] tracking-[0.15em] uppercase px-1.5 py-0.5 rounded bg-[#1F5B3F] text-white font-semibold">Confirmed &amp; locked</span>}
        <span className="ml-auto text-xs text-[#9C8B73]">{when(order.created_at)}</span>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#9C8B73] mb-1">Varieties &amp; quantities</div>
        <div className="space-y-1.5">
          {items.map((i, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input value={i.name} onChange={(e) => setItems(items.map((x, n) => n === idx ? { ...x, name: e.target.value } : x))}
                data-testid={`priority-item-name-${order.id}-${idx}`} className="flex-1 h-9 px-2 border bg-white text-sm" style={{ borderColor: EDGE }} />
              <input type="number" min="0" value={i.qty} onChange={(e) => setItems(items.map((x, n) => n === idx ? { ...x, qty: e.target.value } : x))}
                data-testid={`priority-item-qty-${order.id}-${idx}`} className="w-16 h-9 px-2 border bg-white text-sm text-center" style={{ borderColor: EDGE }} />
              <input type="number" min="0" step="0.01" value={i.unit_price ?? 0} onChange={(e) => setItems(items.map((x, n) => n === idx ? { ...x, unit_price: e.target.value } : x))}
                className="w-20 h-9 px-2 border bg-white text-sm text-center" style={{ borderColor: EDGE }} />
              <button type="button" onClick={() => setItems(items.filter((_, n) => n !== idx))} aria-label="Remove item"
                data-testid={`priority-item-remove-${order.id}-${idx}`} className="p-2 text-[#A33030]"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button type="button" onClick={() => setItems([...items, { name: "", qty: 1, unit_price: 0 }])} data-testid={`priority-item-add-${order.id}`}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#6E1E3A]">
            <Plus className="w-3.5 h-3.5" /> Add variety
          </button>
        </div>
        {order.hq_picks_note && <p className="text-xs italic text-[#6E1E3A] mt-2">HQ picks note: {order.hq_picks_note}</p>}
      </div>

      <label className="block text-xs">
        <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Backup varieties</span>
        <input value={backups} onChange={(e) => setBackups(e.target.value)} data-testid={`priority-backups-${order.id}`}
          className="w-full h-9 px-2 border bg-white text-sm" style={{ borderColor: EDGE }} />
      </label>

      <div className="grid gap-2 sm:grid-cols-3">
        <label className="block text-xs">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Payment status</span>
          <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })}
            data-testid={`priority-payment-status-${order.id}`} className="w-full h-9 px-2 border bg-white text-sm" style={{ borderColor: EDGE }}>
            <option value="unpaid">Unpaid</option>
            <option value="partially_paid">Partially paid</option>
            <option value="paid">Paid</option>
          </select>
        </label>
        <Money label="Order total" value={form.total} onChange={(v) => setForm({ ...form, total: v })} testid={`priority-total-${order.id}`} />
        <Money label="Amount paid" value={form.amount_paid} onChange={(v) => setForm({ ...form, amount_paid: v })} testid={`priority-paid-${order.id}`} />
        <Money label="Shipping" value={form.shipping_price} onChange={(v) => setForm({ ...form, shipping_price: v })} />
        <Money label="Discount" value={form.discount} onChange={(v) => setForm({ ...form, discount: v })} />
        <div className="text-xs">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Amount owing</span>
          <div className="h-9 flex items-center font-serif text-lg text-[#A33030]" data-testid={`priority-owing-${order.id}`}>NZ${owing.toFixed(2)}</div>
        </div>
      </div>

      <label className="block text-xs">
        <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Shipping address</span>
        <input value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
          data-testid={`priority-address-${order.id}`} className="w-full h-9 px-2 border bg-white text-sm" style={{ borderColor: EDGE }} />
      </label>
      {order.customer_notes && <p className="text-xs italic text-[#6E1E3A]">Customer said: {order.customer_notes}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-xs">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Customer-visible note</span>
          <textarea value={form.customer_visible_notes} onChange={(e) => setForm({ ...form, customer_visible_notes: e.target.value })} rows={2}
            data-testid={`priority-visible-notes-${order.id}`} className="w-full p-2 border bg-white text-sm" style={{ borderColor: EDGE }} />
        </label>
        <label className="block text-xs">
          <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">Internal note</span>
          <textarea value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} rows={2}
            data-testid={`priority-admin-notes-${order.id}`} className="w-full p-2 border bg-white text-sm" style={{ borderColor: EDGE }} />
        </label>
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={save} disabled={busy} data-testid={`priority-save-${order.id}`}
          className="h-10 px-5 bg-[#6E1E3A] text-white text-[11px] font-bold uppercase tracking-[0.14em] inline-flex items-center gap-2 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save order
        </button>
      </div>
    </article>
  );
}

const Money = ({ label, value, onChange, testid }) => (
  <label className="block text-xs">
    <span className="block text-[10px] tracking-[0.2em] uppercase text-[#9C8B73] mb-1">{label}</span>
    <input type="number" min="0" step="0.01" value={value ?? 0} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full h-9 px-2 border bg-white text-sm" style={{ borderColor: EDGE }} />
  </label>
);
